/**
 * AccountSelectorModal — Modal para asignar cuentas Meta a una marca.
 *
 * Flujo:
 * 1. Se abre con identity_id + brand_id
 * 2. Carga ad_accounts, pages e instagram_accounts desde
 *    /api/v2/oauth/meta/identities/{identity_id}/accounts
 * 3. User selecciona checkboxes
 * 4. Al "Asignar" hace POST /api/v2/connections por cada seleccion
 * 5. onSuccess({created: N}) para que el padre refresque conexiones
 *
 * Props:
 * - open (boolean)
 * - onOpenChange (fn)
 * - identityId (string)
 * - brandId (string)
 * - existingConnections (array)  -- para marcar ya conectadas como disabled
 * - onSuccess (fn)
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Facebook, Instagram, FileText, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { oauthApi, connectionsApi } from '@/components/v2/api';


export default function AccountSelectorModal({
  open,
  onOpenChange,
  identityId,
  brandId,
  existingConnections = [],
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState({
    ad_accounts: [],
    pages: [],
    instagram_accounts: [],
  });

  // Selecciones: {ad_accounts: Set, pages: Set, instagram_accounts: Set}
  const [selected, setSelected] = useState({
    ad_accounts: new Set(),
    pages: new Set(),
    instagram_accounts: new Set(),
  });

  const [saving, setSaving] = useState(false);

  // Set de IDs ya conectados (para deshabilitar checkboxes)
  const alreadyConnected = new Set(
    existingConnections.map(c => c.external_account_id)
  );

  // Cargar cuentas al abrir
  const loadAccounts = useCallback(async () => {
    if (!identityId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await oauthApi.metaListAccounts(identityId);
      setAccounts({
        ad_accounts: data.ad_accounts || [],
        pages: data.pages || [],
        instagram_accounts: data.instagram_accounts || [],
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [identityId]);

  useEffect(() => {
    if (open && identityId) {
      // Reset y cargar
      setSelected({
        ad_accounts: new Set(),
        pages: new Set(),
        instagram_accounts: new Set(),
      });
      loadAccounts();
    }
  }, [open, identityId, loadAccounts]);

  const toggleSelection = (type, id) => {
    setSelected(prev => {
      const newSet = new Set(prev[type]);
      if (newSet.has(id)) newSet.delete(id);  // id = external_account_id
      else newSet.add(id);
      return { ...prev, [type]: newSet };
    });
  };

  const totalSelected =
    selected.ad_accounts.size +
    selected.pages.size +
    selected.instagram_accounts.size;

  const handleSubmit = async () => {
    if (totalSelected === 0) {
      toast.error('Selecciona al menos una cuenta');
      return;
    }
    setSaving(true);

    const errors = [];
    let created = 0;

    // Helper para crear una connection
    const createConn = async (service, externalId, externalName, metadata) => {
      try {
        await connectionsApi.create({
          client_id: brandId,
          oauth_identity_id: identityId,
          service,
          external_account_id: externalId,
          external_account_name: externalName,
          metadata: metadata || {},
        });
        created++;
      } catch (e) {
        errors.push(`${externalName}: ${e?.response?.data?.detail || e.message}`);
      }
    };

    // Ad accounts
    for (const eid of selected.ad_accounts) {
      const acc = accounts.ad_accounts.find(a => a.external_account_id === eid);
      if (!acc) continue;
      await createConn('meta_ads', acc.external_account_id, acc.external_account_name, acc.metadata);
    }

    // Pages
    for (const eid of selected.pages) {
      const p = accounts.pages.find(x => x.external_account_id === eid);
      if (!p) continue;
      await createConn('facebook_page', p.external_account_id, p.external_account_name, p.metadata);
    }

    // Instagram
    for (const eid of selected.instagram_accounts) {
      const ig = accounts.instagram_accounts.find(x => x.external_account_id === eid);
      if (!ig) continue;
      await createConn('instagram_business', ig.external_account_id, ig.external_account_name, ig.metadata);
    }

    setSaving(false);

    if (errors.length > 0) {
      toast.error(`${created} creadas, ${errors.length} con error`);
      console.error('Errores creando conexiones:', errors);
    } else {
      toast.success(`${created} ${created === 1 ? 'cuenta asignada' : 'cuentas asignadas'} a la marca`);
    }

    onSuccess?.({ created, errors });
    onOpenChange(false);
  };

  // ============ RENDER HELPERS ============

  const renderAccountList = (type, items, Icon, getLabel, getSub) => {
    if (!items || items.length === 0) {
      return (
        <p className="text-sm text-slate-500 py-2">No hay {type} disponibles</p>
      );
    }
    return (
      <div className="space-y-1.5">
        {items.map(item => {
          const eid = item.external_account_id;
          const isAlreadyConnected = alreadyConnected.has(eid);
          const isSelected = selected[type].has(eid);
          return (
            <label
              key={eid}
              className={`flex items-center gap-3 p-2.5 rounded-md border transition ${
                isAlreadyConnected
                  ? 'bg-slate-800/40 border-slate-700 cursor-not-allowed opacity-60'
                  : isSelected
                    ? 'bg-blue-500/10 border-blue-500/40 cursor-pointer'
                    : 'bg-slate-900 border-slate-800 hover:bg-slate-800/60 cursor-pointer'
              }`}
            >
              <Checkbox
                checked={isAlreadyConnected || isSelected}
                disabled={isAlreadyConnected}
                onCheckedChange={() => !isAlreadyConnected && toggleSelection(type, eid)}
              />
              <Icon className="h-4 w-4 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">
                  {getLabel(item)}
                </div>
                <div className="text-xs text-slate-400 truncate">
                  {getSub(item)}
                </div>
              </div>
              {isAlreadyConnected && (
                <Badge variant="outline" className="text-[10px] shrink-0">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Conectada
                </Badge>
              )}
            </label>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Asignar cuentas Meta a la marca</DialogTitle>
          <DialogDescription>
            Selecciona qué Ad Accounts, Pages e Instagram Business quieres conectar.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-6">
          {loading && (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-sm">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-red-300">Error cargando cuentas</div>
                <div className="text-red-200/80 text-xs mt-0.5">{error}</div>
              </div>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Ad Accounts */}
              <section>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Facebook className="h-4 w-4" /> Ad Accounts
                  <span className="text-xs font-normal text-slate-500">
                    ({accounts.ad_accounts.length})
                  </span>
                </h3>
                {renderAccountList(
                  'ad_accounts',
                  accounts.ad_accounts,
                  Facebook,
                  (a) => a.external_account_name || '(sin nombre)',
                  (a) => `${a.external_account_id} · ${a.metadata?.currency || ''} · status: ${a.metadata?.status ?? 'N/A'}`
                )}
              </section>

              {/* Pages */}
              <section>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Facebook Pages
                  <span className="text-xs font-normal text-slate-500">
                    ({accounts.pages.length})
                  </span>
                </h3>
                {renderAccountList(
                  'pages',
                  accounts.pages,
                  FileText,
                  (p) => p.external_account_name || '(sin nombre)',
                  (p) => `${p.external_account_id} · ${p.metadata?.category || 'Sin categoria'}`
                )}
              </section>

              {/* Instagram */}
              <section>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Instagram className="h-4 w-4" /> Instagram Business
                  <span className="text-xs font-normal text-slate-500">
                    ({accounts.instagram_accounts.length})
                  </span>
                </h3>
                {renderAccountList(
                  'instagram_accounts',
                  accounts.instagram_accounts,
                  Instagram,
                  (ig) => ig.external_account_name ? `@${ig.external_account_name}` : '(sin username)',
                  (ig) => `${ig.external_account_id}${ig.metadata?.page_name ? ' · page: ' + ig.metadata.page_name : ''}`
                )}
              </section>
            </>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <div className="flex-1 text-sm text-slate-400">
            {totalSelected > 0 && `${totalSelected} cuenta${totalSelected > 1 ? 's' : ''} seleccionada${totalSelected > 1 ? 's' : ''}`}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving || totalSelected === 0 || loading}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Asignando...
              </>
            ) : (
              `Asignar ${totalSelected} cuenta${totalSelected !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
